import { Link } from 'react-router-dom';
import { Box, Button, Grid, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import styles from '../components/muiStyles';
import colors from '../theme/colors'; // Added import
import { qsaiImages, MAX_FILES } from '../constants/index';
import AppHeader from '../components/AppHeader';
import HeroSection from '../components/HeroSection';

const IntroPage = () => {
  return (
    <Box sx={styles.page}>
      <Grid container>
        <Grid item xs={12}>
          <AppHeader />
        </Grid>
        <Grid item xs={12}>
          <HeroSection />
        </Grid>

        {/* "See QSAI in Action" Section (Video) */}
        <Grid item xs={12} sx={styles.actionVideoSection}>
          <Box sx={styles.limitWidth}>
            <Typography sx={styles.actionVideoSection.title}>
              See QSAI in Action:
            </Typography>
            <Box sx={styles.actionVideoSection.videoContainer}>
              <iframe
                src="https://www.loom.com/embed/c14e012e6d3041fca1d49657cf7f6299?sid=50166a8d-2e9d-43a0-b561-a7a5e3135d99&hide_speed=true&hideEmbedTopBar=true"
                frameBorder="0"
                webkitallowfullscreen="true"
                mozallowfullscreen="true"
                allowFullScreen
                style={styles.actionVideoSection.iframe}
                title="QSAI Product Demo"
              />
            </Box>
          </Box>
        </Grid>

        {/* Testimonial Section */}
        <Grid item xs={12} sx={styles.testimonialSection}>
          <Box sx={styles.limitWidth}>
            <Typography sx={styles.testimonialSection.quote}>
              &quot;QS AI is insane! I&apos;ve just used it on 3 quotes and
              it&apos;s really accurate against a comparison I&apos;ve
              completed&quot;
            </Typography>
            <Typography sx={styles.testimonialSection.author}>
              — Chris R - Vistry
            </Typography>
          </Box>
          <Box sx={styles.limitWidth}>
            <Typography sx={styles.testimonialSection.quote}>
              &quot;You&apos;re one step ahead using QSAI&quot;
            </Typography>
            <Typography sx={styles.testimonialSection.author}>
              — Liam Dolan - Berkeley Group (St Edward)
            </Typography>
          </Box>
        </Grid>

        {/* "Why Choose QSAI" Section */}
        <Grid item xs={12} sx={styles.whyChooseSection}>
          <Box sx={styles.limitWidth}>
            <Typography sx={styles.whyChooseSection.title}>
              Why Choose{' '}
              <Typography
                component="span"
                sx={styles.whyChooseSection.title.span}
              >
                QSAI
              </Typography>
            </Typography>
            <Box sx={styles.whyChooseSection.list} component="ul">
              {[
                'Streamline the subcontractor quote review process.',
                'Identify and highlight any discrepancies between quotes.',
                'Easily request clarification or better pricing from subcontractors.',
              ].map((text, index) => (
                <Typography
                  sx={styles.whyChooseSection.list.listItem}
                  component="li"
                  key={index}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <CheckCircleIcon
                      sx={{
                        ...styles.whyChooseSection.list.checkmark,
                        mt: { xs: 0.3, md: 0.5 },
                      }}
                    />
                    <Box
                      component="span"
                      sx={styles.whyChooseSection.list.textSpan}
                    >
                      {text}
                    </Box>
                  </Box>
                </Typography>
              ))}
            </Box>
          </Box>
        </Grid>

        {/* How It Works Section */}
        <Grid item xs={12} sx={styles.works}>
          <Box sx={styles.limitWidth}>
            <Typography sx={styles.works.title}>How it works</Typography>
            <Grid container spacing={3} sx={styles.howItWorksIcons}>
              {[
                {
                  title: 'Upload Quotes',
                  description: `Drag and drop your quotes or select up to ${MAX_FILES} quotes`,
                },
                {
                  title: 'AI Driven Analysis',
                  description:
                    'Get a detailed comparison and find discrepancies',
                },
                {
                  title: 'Take Action',
                  description:
                    'Send requests for clarification or better pricing',
                },
              ].map((entry, index) => (
                <Grid item xs={12} md={4} key={index} sx={styles.icon}>
                  <img
                    className={`icon-${index + 1}`}
                    src={`${qsaiImages}/how-${index + 1}.svg`}
                    alt={entry.title}
                    style={{
                      ...styles.icon.img,
                      ...(index === 0 && { height: '83px', width: '83px' }),
                      ...(index === 1 && { height: '83px', width: '121px' }),
                      ...(index === 2 && { height: '83px', width: '156px' }),
                    }}
                  />
                  <Typography variant="h6" sx={styles.icon.title}>
                    {entry.title}
                  </Typography>
                  <Typography variant="body2" sx={styles.icon.description}>
                    {entry.description}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>

        {/* Unlock Section */}
        <Grid item xs={12} sx={styles.unlock}>
          <Box sx={styles.limitWidth}>
            <Typography sx={styles.unlock.title} variant="h3">
              Unlock Powerful{' '}
              <Typography component="span">AI-Driven Tools </Typography>
              for Smarter Decisions
            </Typography>

            <Grid container spacing={4} sx={{ mb: 6 }}>
              <Grid item xs={12} md={6}>
                <img
                  style={styles.unlock.image}
                  src={`${qsaiImages}/unlock-1.svg`}
                  alt="Best Subcontractor Comparison"
                />
              </Grid>
              <Grid item xs={12} md={6} sx={styles.unlock.textSection}>
                <Typography sx={styles.unlock.textSection.title}>
                  Best Subcontractor Comparison
                </Typography>
                <Typography sx={styles.unlock.textSection.subtitle}>
                  Simply upload your three chosen quotes in pdf/excel format
                </Typography>
              </Grid>
            </Grid>

            <Grid
              container
              spacing={4}
              sx={{ mb: 6, ...styles.unlock.rowReverse }}
            >
              <Grid item xs={12} md={6} sx={styles.unlock.textSection}>
                <Typography
                  className="blue"
                  sx={styles.unlock.textSection.title}
                >
                  Spot Discrepancies Instantly
                </Typography>
                <Typography sx={styles.unlock.textSection.subtitle}>
                  In just a few minutes QSAI will do all the heavy lifting for
                  you
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <img
                  style={styles.unlock.image}
                  src={`${qsaiImages}/unlock-2.svg`}
                  alt="Spot Discrepancies Instantly"
                />
              </Grid>
            </Grid>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <img
                  style={styles.unlock.image}
                  src={`${qsaiImages}/unlock-3.svg`}
                  alt="Review Report"
                />
              </Grid>
              <Grid item xs={12} md={6} sx={styles.unlock.textSection}>
                <Typography
                  className="orange"
                  sx={styles.unlock.textSection.title}
                >
                  Review Detailed Reports
                </Typography>
                <Typography sx={styles.unlock.textSection.subtitle}>
                  Review your report to identify variations and additional value
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Download Section */}
        <Grid
          item
          xs={12}
          className="download-section"
          sx={{
            ...styles.header,
            py: { xs: 6, md: 8 },
            background: `linear-gradient(180deg, ${colors.brand.darker}, ${colors.brand.dark})`, // Updated
          }}
        >
          <Box sx={styles.limitWidth}>
            <Typography
              sx={{
                ...styles.header.h1,
                fontSize: { xs: '34px', md: '40px' },
                mb: 4,
              }}
            >
              <Typography component="span" sx={styles.header.h1.span}>
                Looking for multiple licenses?
              </Typography>
            </Typography>
            <Button
              variant="contained"
              component={Link}
              to="/help-support"
              sx={{
                ...styles.button,
                ...styles.button.login,
                ...styles.button.largeSignup,
                fontSize: { xs: '18px', md: '20px' },
              }}
            >
              Get in touch with our team
            </Button>
          </Box>
        </Grid>

        {/* Terms and Conditions Section  */}
        <Grid item xs={12} sx={styles.termsSection}>
          <Typography
            component="p"
            sx={{
              fontFamily: 'inherit',
              fontSize: 'inherit',
              color: 'inherit',
            }}
          >
            By signing up, you agree to our{' '}
            <Link to="/terms" style={styles.termsSection.link}>
              Terms and Conditions
            </Link>
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default IntroPage;
